import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import { Container, Row, Col } from 'react-bootstrap';
import { isAuthenticated, hasCompletedOnboarding, getOnboardingRoute } from '../../utils/AuthorizationCheck';
import DashboardVideoCard from '../DashboardVideoCard';
import AISettings from './AISettings';
import { fetchUser } from '../../actions';
//import VideoCardPlaceholder from '../VideoCardPlaceholder';

function Dashboard({auth, fetchUser}) {
    const history = useHistory();
    
    // State for video data
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [hasInProgressJobs, setHasInProgressJobs] = useState(false);
    
    // State for AI settings
    const [platform_league_id, setPlatform_league_id] = useState(null);

    // Fetch platform_league_id for AI settings
    const fetchPlatformLeagueId = useCallback(async () => {
        try {
            const response = await axios.get('/api/get/user-league-data');
            if (response.data.success) {
                setPlatform_league_id(response.data.platform_league_id);
            }
        } catch (error) {
            console.error('Error fetching platform league ID:', error);
        }
    }, []);

    const fetchVideos = useCallback(async () => {
        setLoading(true);
        setError(null);
        setHasInProgressJobs(false);
        
        try {
            // First, try to fetch completed videos
            const response = await axios.get('/api/get/user-videos');
            const completedVideos = response.data.videos || [];
            
            if (completedVideos.length > 0) {
                // We have completed videos, show them
                setVideos(completedVideos);
                setHasInProgressJobs(false);
            } else {
                // No completed videos, check for in-progress jobs
                try {
                    const inProgressResponse = await axios.get('/api/get/user-video-jobs');
                    const hasInProgress = inProgressResponse.data.hasInProgressJobs || false;
                    
                    if (hasInProgress) {
                        // We have in-progress jobs, show placeholder cards
                        setVideos([{ id: 'in-progress', completed: false }]);
                        setHasInProgressJobs(true);
                    } else {
                        // No videos at all
                        setVideos([]);
                        setHasInProgressJobs(false);
                    }
                } catch (inProgressErr) {
                    console.error('Error checking in-progress jobs:', inProgressErr);
                    // If we can't check in-progress jobs, just show no videos
                    setVideos([]);
                    setHasInProgressJobs(false);
                }
            }
        } catch (err) {
            console.error('Error fetching videos:', err);
            setError('Failed to load videos. Please try again.');
            setVideos([]); // Clear videos on error
            setHasInProgressJobs(false);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch data - but only if not coming from payment success
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        const isPaymentSuccess = query.get('success') === 'true';
        
        if (!isPaymentSuccess) {
            // Normal dashboard load - fetch videos and platform league ID immediately
            fetchVideos();
            fetchPlatformLeagueId();
        }
        // If payment success, let the payment flow handle video fetching
    }, [fetchVideos, fetchPlatformLeagueId, auth]);

    // Handle payment success
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        const isPaymentSuccessFromURL = query.get('success') === 'true';
        
        if (isPaymentSuccessFromURL) {
            // Handle payment success
            setIsProcessingPayment(true);
            
            const updateAuthState = async () => {
                try {
                    // Update database
                    await axios.post('/api/post/mark-onboarding-complete');
                    
                    // Create video job for new user
                    try {
                        await axios.post('/api/post/create-new-video-job');
                        console.log('✅ Video job created successfully for new user');
                    } catch (videoJobError) {
                        console.error('❌ Error creating video job:', videoJobError);
                        // Don't fail the entire flow if video job creation fails
                    }
                    
                    // Now fetch videos and platform league ID
                    await fetchVideos();
                    await fetchPlatformLeagueId();
                    
                    // Refresh Redux with fresh server data
                    await fetchUser();
                    
                    // Payment success - stay on dashboard (no redirect)
                    setIsProcessingPayment(false);
                    
                    // Clean up URL to remove success parameter
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, document.title, newUrl);
                } catch (error) {
                    console.error('Error updating auth state:', error);
                    setIsProcessingPayment(false);
                }
            };
            
            updateAuthState();
        }
    }, [fetchUser, fetchVideos, fetchPlatformLeagueId]);

    // Show loading state while processing payment
    if (isProcessingPayment) {
        return (
            <div className="dashboard-container">
                <Container fluid className="px-4">
                    <Row className="mt-5 mb-4">
                        <Col>
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="mt-3">
                                    <h5>Loading your dashboard...</h5>
                                    <p className="text-muted">Please wait while we fetch your data.</p>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // Check URL first - if payment success, show content immediately
    const query = new URLSearchParams(window.location.search);
    const isPaymentSuccessFromURL = query.get('success') === 'true';
    
    if (isPaymentSuccessFromURL) {
        // Show content immediately - don't wait for auth
        // Content will be rendered below
    } else {
        // Wait for Redux to hydrate before doing auth checks
        if (!auth.hydrated) {
            return (
                <div className="dashboard-container">
                    <Container fluid className="px-4">
                        <Row className="mt-5 mb-4">
                            <Col>
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <div className="mt-3">
                                        <h5>Loading...</h5>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            );
        }
        
        // Only redirect if not authenticated
        if (!isAuthenticated(auth)) {
            history.push('/');
            return null;
        }
        
        // Check onboarding completion        
        if (!hasCompletedOnboarding(auth)) {
            const onboardingRoute = getOnboardingRoute(auth);
            history.push(onboardingRoute);
            return null;
        }
    }

        return (
            <div className="dashboard-container">
                <Container fluid className="px-4">
                    {/* AI Settings */}
                    <Row className="mt-5 mb-2">
                        <Col>
                            <AISettings 
                                auth={auth} 
                                platform_league_id={platform_league_id} 
                            />
                        </Col>
                    </Row>

                    {/* Video Grid */}
                    <Row className="mb-4">
                        <Col>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="text-center py-5">
                                    <div className="alert alert-danger" role="alert">
                                        <h5>Unable to load videos</h5>
                                        <p>{error}</p>
                                        <button 
                                            className="btn btn-primary" 
                                            onClick={fetchVideos}
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                </div>
                            ) : videos.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="alert alert-info" role="alert">
                                        <h5>No videos available</h5>
                                        <p>Your weekly recap videos will appear here once they're generated.</p>
                                    </div>
                                </div>
                            ) : hasInProgressJobs ? (
                                <div className="text-center py-5">
                                    <div className="alert alert-warning" role="alert">
                                        <h5>Video in progress</h5>
                                        <p>We're working on creating your video. It should be ready in about 10-15 minutes.</p>
                                    </div>
                                </div>
                            ) : (
                                <Row className="g-4">
                                    {videos.map((video) => (
                                        <Col key={video.id} xs={12} md={6} lg={4}>
                                            <DashboardVideoCard
                                                video={video}
                                                onDownload={() => {
                                                    // Download is now handled by DashboardVideoCard component
                                                    console.log('Download handled by DashboardVideoCard component');
                                                }}
                                                onShare={() => {
                                                    // Share functionality can be implemented here if needed
                                                    console.log('Share clicked for:', video.title);
                                                }}
                                            />
                                        </Col>
                                    ))}
                                    
                                    {/* Next Week Placeholder */}
                                    {/* <Col xs={12} md={6} lg={4}>
                                        <VideoCardPlaceholder nextWeekETA="Tuesday BY 1:00pm ET" />
                                    </Col> */}
                                </Row>
                            )}
                        </Col>
                    </Row>


                </Container>
            </div>
        );
}

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps, {fetchUser})(Dashboard);

